// nav/room-url.js: Phase 3 #7: shareable ?room=CODE URL helpers + the
// one-shot auto-join loop guard.
//
// Pure, framework-agnostic (mirrors overlay-nav.js): the
// Preact side (useRoomWs's effect, the Share/Copy buttons) consumes these
// helpers, but ALL of the loop-guard logic and URL formatting lives here so
// it stays unit-testable without JSX.
//
// THE RISK this module neutralizes (codex-flagged): an auto-join from
// ?room=CODE that re-fires on every render/effect re-run, looping forever
// or surfacing a stream of ALREADY_CONNECTED errors. shouldAutoJoin()
// encodes the entire guard as a pure truth table; the hook pairs it with a
// one-shot ref so it can fire at most once per page load.

const ROOM_CODE_LENGTH = 6;
// Mirrors server/src/room/code-gen.js + RoomLobby's sanitizeCodeInput,
// excludes confusables 0/O/1/I/L. Keep these three in sync.
const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * Uppercase + strip everything outside the room-code alphabet, capped at
 * ROOM_CODE_LENGTH. Returns the (possibly short) sanitized string.
 * @param {unknown} raw
 * @returns {string}
 */
function sanitizeCode(raw) {
  const upper = String(raw ?? '').toUpperCase();
  let out = '';
  for (const ch of upper) {
    if (ROOM_CODE_ALPHABET.includes(ch)) out += ch;
    if (out.length === ROOM_CODE_LENGTH) break;
  }
  return out;
}

/**
 * Build the full shareable URL for a room code, e.g.
 *   roomShareUrl('abc234', 'https://euchre.dev') → 'https://euchre.dev/?room=ABC234'
 * The code is normalized (uppercased, confusables stripped). Returns null
 * if the code doesn't sanitize to a full 6-char code, so callers never
 * advertise a junk URL.
 * @param {unknown} code
 * @param {string} origin  e.g. window.location.origin
 * @returns {string|null}
 */
export function roomShareUrl(code, origin) {
  const normalized = sanitizeCode(code);
  if (normalized.length !== ROOM_CODE_LENGTH) return null;
  const base = String(origin ?? '').replace(/\/+$/, '');
  return `${base}/?room=${normalized}`;
}

/**
 * Extract a normalized room code from a URL query string (location.search).
 * Returns the 6-char uppercased code, or null when absent or unrecoverable
 * (garbage / too short). A too-LONG value collapses to its first 6 valid
 * chars, matching the join input's own truncation behavior.
 * @param {string} search  e.g. window.location.search ('?room=ABC234')
 * @returns {string|null}
 */
export function parseRoomCodeFromUrl(search) {
  let raw = null;
  try {
    raw = new URLSearchParams(search || '').get('room');
  } catch {
    return null;
  }
  if (raw == null) return null;
  const normalized = sanitizeCode(raw);
  return normalized.length === ROOM_CODE_LENGTH ? normalized : null;
}

/**
 * The loop guard. Returns true ONLY when the URL carries a code that we
 * have not yet attempted to auto-join AND we are not already sitting in
 * that same room. Every other case is false:
 *
 *   urlCode | hasAttempted | inRoom | currentRoomCode | → result
 *   --------+--------------+--------+-----------------+---------
 *   null    | -            | -      | -               | false   (no code in URL)
 *   ABC234  | false        | false  | -               | TRUE    (fresh load, free)
 *   ABC234  | true         | -      | -               | false   (one-shot already fired: reload/effect re-run/back-forward)
 *   ABC234  | false        | true   | ABC234          | false   (already in that room: share-while-in-room / 2nd tab → ALREADY_CONNECTED no-op)
 *   ABC234  | false        | true   | ZZZ999          | false   (in a DIFFERENT room: don't yank the user out)
 *
 * Code comparison is case-insensitive. This is the only place the guard
 * logic lives; the hook just wires the ref + dispatches the join.
 * @param {{urlCode: string|null, hasAttempted: boolean, inRoom: boolean, currentRoomCode: string|null}} ctx
 * @returns {boolean}
 */
export function shouldAutoJoin({ urlCode, hasAttempted, inRoom, currentRoomCode }) {
  if (!urlCode) return false;
  if (hasAttempted) return false;
  // Treat "in a room" as either the status flag OR a concrete current code,
  // referencing currentRoomCode makes the same-room / second-tab case
  // (which the server would reject with ALREADY_CONNECTED) an explicit input
  // to the guard, not just an implicit consequence of `inRoom`.
  if (inRoom || currentRoomCode) {
    // In any room: never auto-join. SAME room → share-while-in-room /
    // second-tab no-op; DIFFERENT room → don't yank the user out.
    return false;
  }
  return true;
}

/**
 * Share a room URL using the Web Share API when available, else copy it to
 * the clipboard. Dependencies are injected so this is unit-testable:
 *   - nav.share(data)       → the Web Share API (optional)
 *   - clipboard.writeText() → the clipboard fallback
 *
 * Returns:
 *   'shared' navigator.share resolved
 *   'copied' fell back to the clipboard (share absent or threw non-abort)
 *   'cancelled' the user dismissed the share sheet (AbortError); NO copy
 * @param {string} url
 * @param {{nav?: {share?: (data: {url: string}) => Promise<void>}, clipboard?: {writeText?: (t: string) => Promise<void>}}} deps
 * @returns {Promise<'shared'|'copied'|'cancelled'>}
 */
export async function shareRoom(url, { nav, clipboard } = {}) {
  if (nav && typeof nav.share === 'function') {
    try {
      await nav.share({ url });
      return 'shared';
    } catch (err) {
      // A deliberate user cancel is NOT a failure, don't silently copy
      // behind their back. Any other error falls through to clipboard.
      if (err && /** @type {any} */ (err).name === 'AbortError') return 'cancelled';
    }
  }
  if (clipboard && typeof clipboard.writeText === 'function') {
    await clipboard.writeText(url);
  }
  return 'copied';
}
